<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Text, Popover, Container, Icon } from '@Shelly/shelly-design-system';
	import type { EnergyTariff } from 'src/types/EnergyTariff';

	export let benchmarkTariff: EnergyTariff | null = null;

	// Helper function to format numbers with comma as decimal separator
	const formatNumber = (value: number, decimals: number = 2): string => {
		return value.toFixed(decimals).replace('.', ',');
	};

	// Calculate monthly average
	$: monthlyAverage = benchmarkTariff?.totalCosts
		? formatNumber(benchmarkTariff.totalCosts / 12)
		: '0,00';

	let showMonthlyAveragePopover = false;
</script>

{#if benchmarkTariff}
	<Container type="primary-10" padding="xs" class="mb-m">
		<Text styleType="body" class="text-bold mb-m">
			{$format('energy_price_benchmark_tariff_title', {
				default: 'Ihr Vergleichstarif zur Berechnung der Einsparungen',
				locale: 'de',
			})}
		</Text>
		<div class="tariff-card">
			<div class="tariff-content">
				<div class="tariff-left-column">
					{#if benchmarkTariff.providerInfo}
						<div class="info-row">
							<Text styleType="body-extra-small" class="info-label">
								{$format('label_provider', { default: 'Anbieter', locale: 'de' })}:
							</Text>
							<Text styleType="body-extra-small" class="info-value">
								{benchmarkTariff?.providerInfo?.longName}
							</Text>
						</div>
					{/if}

					{#if benchmarkTariff.name && benchmarkTariff.name !== benchmarkTariff.provider}
						<div class="info-row">
							<Text styleType="body-extra-small" class="info-label">
								{$format('label_rate', { default: 'Tarif', locale: 'de' })}:
							</Text>
							<Text styleType="body-extra-small" class="info-value">
								{benchmarkTariff.name}
							</Text>
						</div>
					{/if}
				</div>

				<div class="tariff-right-column">
					{#if benchmarkTariff.cost?.unitPrice !== undefined}
						<div class="info-row">
							<Text styleType="body-extra-small" class="info-label">
								{benchmarkTariff.cost?.unitPrice?.caption?.text}:
							</Text>
							<Text styleType="body-extra-small" class="info-value price-bold text-bold">
								{benchmarkTariff.cost?.unitPrice?.content?.text}
							</Text>
						</div>
					{/if}
					{#if benchmarkTariff.cost?.totalCost?.totalCostItem !== undefined}
						<div class="info-row">
							<Text styleType="body-extra-small" class="info-label">
								{benchmarkTariff.cost.totalCost?.totalCostItem?.caption?.text}:
							</Text>
							<Text styleType="body-extra-small" class="info-value price-bold text-bold">
								{benchmarkTariff.cost.totalCost?.totalCostItem?.content?.text}
							</Text>
						</div>
					{/if}
				</div>

				<div class="tariff-monthly-average">
					<Text styleType="h1" class="monthly-amount text-bold">
						{monthlyAverage} €
					</Text>
					<div class="monthly-average-info">
						<Text styleType="body-extra-small" class="monthly-label">
							{$format('label_average_per_month', {
								default: 'Durchschnitt pro Monat',
								locale: 'de',
							})}
						</Text>
						<Popover
							placement="top"
							type="hover"
							on:close={() => (showMonthlyAveragePopover = false)}
							hideSlotFooter
							hideSlotStaticTop
							hideCloseBtn
							show={showMonthlyAveragePopover}
							title={$format('energy_price_monthly_average_info_title', {
								default: 'Kosten des Vergleichstarifs',
								locale: 'de',
							})}
						>
							<Icon
								icon="fa-solid fa-circle-info"
								variant="help"
								size="s"
								class="info-icon"
								on:click={() => {
									showMonthlyAveragePopover = !showMonthlyAveragePopover;
								}}
								aria-label={$format('energy_price_monthly_average_info', {
									default: 'Informationen zum monatlichen Durchschnitt',
									locale: 'de',
								})}
							/>
							<svelte:fragment slot="customContent">
								<Text styleType="body-extra-small" class="popover-description">
									{$format('energy_price_monthly_average_info_description', {
										default:
											'Als Grundlage der Berechnung der monatlichen Kosten des Vergleichstarifs dienen die aktuellen Preise des ausgewählten derzeitigen Tarifs. Diese können von den Kosten abweichen, die Sie aktuell zahlen. Sofern es sich um einen Grundversorgungstarif handelt, kann es im der Vergleichsberechnung zugrunde liegenden Zeitraum zu Preisanpassungen der Vergleichstarife kommen. In diesem Fall ändern sich die angegebenen monatlichen Kosten des Vergleichstarifs sowie die angegebene Preisersparnis.',
										locale: 'de',
									})}
								</Text>
							</svelte:fragment>
						</Popover>
					</div>
				</div>
			</div>

			<div class="tariff-note mt-s">
				<Text styleType="tiny">
					{@html $format('energy_price_benchmark_tariff_note', {
						default:
							'<strong>Hinweis:</strong> Grundversorgung (mit gesetzlich garantierten 2 Wochen Kündigungsfrist) als Vergleichstarif voreingestellt und für Sie jederzeit änderbar. Es kann Preisanpassungen während der Vertragslaufzeit bei der Grundversorgung geben. Hierdurch ändert sich die angegebene Preisersparnis.',
						locale: 'de',
					})}
				</Text>
			</div>
		</div>
	</Container>
{/if}

<style>
	.tariff-card {
		display: flex;
		flex-direction: column;
	}

	.tariff-content {
		display: grid;
		grid-template-columns: 1fr 1fr auto;
		gap: var(--spacing-l);
		align-items: start;
	}

	.tariff-left-column,
	.tariff-right-column {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
	}

	.info-row {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.info-label {
		color: var(--color-text-secondary, #6b7280);
		font-weight: 400;
	}

	.info-value {
		color: var(--color-text-primary, #000000);
		font-weight: 400;
	}

	.info-value.price-bold {
		font-weight: 700;
	}

	.tariff-monthly-average {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: flex-start;
		gap: var(--spacing-xs);
		min-width: 120px;
	}

	.monthly-amount {
		font-size: 32px;
		font-weight: 700;
		line-height: 1;
		color: var(--color-text-primary, #000000);
		margin: 0;
	}

	.monthly-average-info {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
	}

	.monthly-label {
		color: var(--color-text-secondary, #6b7280);
		font-size: 12px;
	}

	.info-icon {
		transform: scale(0.85);
	}

	.popover-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.popover-title {
		font-weight: 600;
		margin: 0;
	}

	.popover-description {
		margin: 0;
		line-height: 1.5;
	}

	.tariff-note {
		background: var(--color-background-info, #e0f2fe);
		padding: var(--spacing-s);
		border-radius: 8px;
	}

	.tariff-note :global(p) {
		margin: 0;
		color: var(--color-text-secondary, #6b7280);
	}

	@media (max-width: 768px) {
		.tariff-content {
			grid-template-columns: 1fr;
			gap: var(--spacing-m);
		}

		.tariff-monthly-average {
			align-items: flex-start;
			width: 100%;
		}

		.monthly-amount {
			font-size: 28px;
		}
	}
</style>
